'use client';

import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { Fragment } from 'react';
import { Select } from '@/components/ui';
import { cn } from '@/lib/utils';
import type { VenuePageItem } from '../../libs/venuePage';
import { VENUE_DOT_CLASSES } from '../(tabs)/schedule/libs/venueColumns';
import { PublicVenueDetail } from './PublicVenueDetail';

export function VenueExplorer({ items, locale }: { items: VenuePageItem[]; locale: string }) {
  const t = useTranslations('Event.venuesPage');
  const params = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();
  const selected = items.find((item) => item.venue.id === params.get('venue')) ?? items[0];
  if (!selected) return null;
  const position = items.indexOf(selected) + 1;
  const rail = items.length >= 4;
  const nextVenue = items[position];
  const choose = (id: string) => {
    if (id === selected.venue.id || !items.some((item) => item.venue.id === id)) return;
    const next = new URLSearchParams(params.toString());
    next.set('venue', id);
    router.push(`${pathname}?${next.toString()}`, { scroll: false });
  };
  return (
    <div data-venue-explorer className="w-full min-w-0 pb-12">
      {items.length > 1 && (
        <div className="mb-4 flex min-w-0 flex-wrap items-center justify-between gap-2 print:hidden">
          <div className="min-w-0 md:hidden">
            <Select
              variant="navigation"
              label={t('chooseVenue')}
              value={selected.venue.id}
              onChange={(e) => choose(e.target.value)}
            >
              {items.map((item) => (
                <option key={item.venue.id} value={item.venue.id}>
                  {item.venue.name}
                </option>
              ))}
            </Select>
          </div>
          <p className="text-muted text-xs">
            {t('summary', {
              venues: items.length,
              courts: items.reduce((count, item) => count + item.courtCount, 0),
            })}
          </p>
          {nextVenue && selected.travelMinToNext !== null && (
            <p
              data-venue-selected-travel
              className="text-muted w-full text-xs break-words md:hidden"
            >
              {t('travelTo', { minutes: selected.travelMinToNext, name: nextVenue.venue.name })}
            </p>
          )}
        </div>
      )}
      <div
        className={cn(
          'min-w-0 print:block',
          rail ? 'md:grid md:grid-cols-[12rem_minmax(0,1fr)] md:items-start md:gap-5' : 'space-y-4',
        )}
      >
        {items.length > 1 && (
          <nav
            aria-label={t('chooseVenue')}
            className={cn(
              'hidden min-w-0 md:grid print:hidden',
              rail
                ? 'gap-1'
                : items.length === 2
                  ? 'grid-cols-[minmax(0,1fr)_minmax(3rem,0.5fr)_minmax(0,1fr)]'
                  : 'grid-cols-[minmax(0,1fr)_minmax(2rem,0.3fr)_minmax(0,1fr)_minmax(2rem,0.3fr)_minmax(0,1fr)]',
            )}
          >
            {items.map((item, i) => {
              const active = item.venue.id === selected.venue.id;
              const next = items[i + 1];
              return (
                <Fragment key={item.venue.id}>
                  <button
                    type="button"
                    aria-pressed={active}
                    onClick={() => choose(item.venue.id)}
                    className="focus-visible:ring-ring flex min-h-11 w-full min-w-0 items-start gap-3 rounded-md px-1 py-2 text-left focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
                  >
                    <span
                      aria-hidden="true"
                      className={cn(
                        'mt-1.5 size-2.5 shrink-0 rounded-full',
                        VENUE_DOT_CLASSES[i % VENUE_DOT_CLASSES.length],
                        active && 'ring-primary ring-offset-background ring-2 ring-offset-2',
                      )}
                    />
                    <span className="min-w-0 flex-1">
                      <span
                        className={cn(
                          'font-heading block text-sm font-semibold break-words',
                          active ? 'text-warning-emphasis' : 'text-foreground',
                        )}
                      >
                        {item.venue.name}
                      </span>
                      <span className="text-muted mt-0.5 block font-mono text-[0.6875rem]">
                        {t('courtCount', { count: item.courtCount })}
                      </span>
                    </span>
                  </button>
                  {next && (
                    <div
                      className={cn(
                        'text-muted min-w-0',
                        rail
                          ? 'border-border ml-2 min-h-5 border-l border-dashed py-1 pl-5'
                          : 'pt-4 pr-4',
                      )}
                    >
                      {!rail && (
                        <div aria-hidden="true" className="border-border border-t border-dashed" />
                      )}
                      {item.travelMinToNext !== null && (
                        <p
                          className={cn(
                            'font-mono text-[0.6875rem] break-words',
                            !rail && 'mt-2 text-center',
                          )}
                        >
                          <span aria-hidden="true">
                            {t('travel', { minutes: item.travelMinToNext })}
                          </span>
                          <span className="sr-only">
                            {t('travelTo', {
                              minutes: item.travelMinToNext,
                              name: next.venue.name,
                            })}
                          </span>
                        </p>
                      )}
                    </div>
                  )}
                </Fragment>
              );
            })}
          </nav>
        )}
        <PublicVenueDetail
          key={selected.venue.id}
          item={selected}
          position={position}
          total={items.length}
          locale={locale}
        />
      </div>
    </div>
  );
}
